// Error reporting, confined to one module — same shape as `clerk.ts`: a build-time key,
// unset means nothing activates, and everything else in the client stays unaware of it.
//
// `VITE_SENTRY_DSN` is a build-time constant like `VITE_CLERK_PUBLISHABLE_KEY` and
// `VITE_MOR_API`. A DSN is meant to be shipped to browsers — it identifies where events go,
// not a secret that gates who can send them.
//
// The environment tag exists because the two Railway environments share one Sentry DSN
// (one project, differentiated by this tag) rather than one project each. It comes from
// `build.ts` rather than its own `VITE_SENTRY_*` variable — one name for one fact, and
// `build.ts`'s `ENVIRONMENT` is that fact, already read from Railway's own
// `RAILWAY_ENVIRONMENT_NAME` at build time. Nothing here guesses it from
// `import.meta.env.MODE`: that reflects the Vite build mode, which is `production` for
// both `vite build` runs regardless of which Railway environment the build ends up in.
//
// `release` is the commit, also from `build.ts`, so a Sentry stack trace names the commit
// it came from rather than a build's number, which is not unique outside production.

import * as Sentry from '@sentry/react';

import { COMMIT, ENVIRONMENT } from './build.js';

const DSN: string = (import.meta.env['VITE_SENTRY_DSN'] ?? '').trim();

export function sentryConfigured(): boolean {
  return DSN !== '';
}

/** A no-op unless a DSN was built in — the same shape as `getClerk()` returning `null`. */
export function initSentry(): void {
  if (!sentryConfigured()) {
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT ?? 'unknown',
    release: COMMIT ?? undefined,
  });
}
